import { Injectable, inject } from '@angular/core';
import {
  BbitLegalFilesResponse,
  BbitString,
  IBbitI18n,
  IBbitLegalFile,
  IBbitPlaceBookingMetaResponse,
} from '@bbitgmbh/bbit.utils';
import { BbitWebsiteService } from '@bbitgmbh/website.sdk';
import { Duration } from 'luxon';
export interface IBbitBookingOption {
  allowDurationChange?: boolean;
  key: string;
  title: IBbitI18n;
  subTitle: IBbitI18n;
  designation: IBbitI18n;
  image: string;
  places?: string[];
  usageTypes?: { _id: string; text: string }[];
  hasPermission?: boolean;
  startOffset?: Duration;
  duration?: Duration;
  snapGrid?: Duration;
}

@Injectable({
  providedIn: 'root',
})
export class PlaceBookingService {
  private _website = inject(BbitWebsiteService);
  private _bookingOptions: IBbitBookingOption[] = [
    {
      key: 'cellar7m',
      subTitle: 'Schiesskeller 1',
      title: '7 Meter',
      designation: 'Faustfeuerwaffen bis max. Kaliber .50AE',
      places: ['#QBBPx9sXESskefYxFWFeK', '#7RRazBd3EA3fa7GgRWD75', '#HhxBRd3zASayRecNgxCnL'],
      image: '/assets/images/halle-1.png',
      allowDurationChange: true,
      duration: Duration.fromObject({ minutes: 30 }),
    },
    {
      key: 'cellar24m',
      subTitle: 'Schiesskeller 2',
      title: '24 Meter',
      designation: 'Faustfeuerwaffen bis max. Kaliber .50AE / Handfeuerwaffen bis max. .223 Rem.',
      places: ['#xQVT43y9Cx9Xfm4UG3dr6', '#jwsdfRsfSz3VVUdaf9JHQ', '#Vjsx8QwnDLxiPinLLE89p'],
      image: '/assets/images/aussenplatz.png',
      allowDurationChange: true,
      duration: Duration.fromObject({ minutes: 30 }),
    },
    {
      key: 'cellar25m',
      subTitle: 'Schiesskeller 3',
      title: '25 Meter',
      designation: 'Faustfeuerwaffen bis max. Kaliber .50AE / Handfeuerwaffen bis max. .338 LM',
      places: ['#JYgaVXd7d7s4cX98FxdQ8', '#8LQzZ8dHATQNrWCC4MbeM', '#4LJrx6r8hAds62jtLcTr6', '#pCsaE97t6wPKBbYnYK43Z'],
      image: '/assets/images/aussenplatz.png',
      allowDurationChange: true,
      duration: Duration.fromObject({ minutes: 30 }),
    },
  ];

  public async getBookingOptions(): Promise<IBbitBookingOption[]> {
    const meta = await this.getMeta();
    return this._bookingOptions.map((option) => {
      return {
        ...option,
        hasPermission: option.places?.some((place) => meta.allowedPlaces.includes(place)),
      };
    });
  }

  public getBookingOption(key: string): IBbitBookingOption | undefined {
    return this._bookingOptions.find((option) => option.key === key);
  }

  public async getMeta(params?: { place?: string; usageType?: string }): Promise<IBbitPlaceBookingMetaResponse> {
    const metaResponse = await fetch(`${this._website.getBlApiUrl()}/placeBooking/meta`, {
      method: 'POST',
      headers: {
        Authorization: `${this._website.getAccessToken()}`,
      },
      body: params ? JSON.stringify(params) : undefined,
    });
    const meta = await metaResponse.json();
    return meta;
  }

  public async getLegalFiles(): Promise<BbitLegalFilesResponse> {
    const response = await fetch(`${this._website.getBlApiUrl()}/placeBooking/legalFiles`, {
      method: 'GET',
    });
    if (!response.ok) {
      throw new Error('Failed to fetch legal files');
    }
    const files = await response.json();
    return files;
  }

  public getDownloadUrl(file: IBbitLegalFile): string {
    return `${this._website.getBlApiUrl()}/placeBooking/legalFile/${BbitString.stripBeginningHashtag(file._id)}`;
  }

}
